import { useCallback, type MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { graphql, useFragment, useMutation } from 'react-relay';
import { setMultiCartItem } from '../actions/cartActions.ts';

import { type useAddItemToCart_item$key } from './__generated__/useAddItemToCart_item.graphql.ts';

const itemFragment = graphql`
    fragment useAddItemToCart_item on Item {
        serviceId
        defaultSkuId @required(action: LOG)
    }
`;

export const userCartItemAddMutation = graphql`
    mutation useAddItemToCart_userCartItemAddMutation($input: UserCartItemAddOrUpdateInput!) {
        userCartItemAddOrUpdate(input: $input) {
            cartItem {
                skuId
            }
        }
    }
`;

export const useAddItemToCart = ({
    userId,
    item: itemRef,
    skuId,
    quantitySelected,
    onClick,
}: {
    userId: string | null;
    item: useAddItemToCart_item$key | null;
    skuId: string | null;
    quantitySelected: string;
    onClick?: (event?: MouseEvent<HTMLElement>) => void;
}): {
    addItemToCart: ({
        type,
        event,
    }: {
        type?: 'CHECKOUT' | 'SAVE_FOR_LATER';
        event?: MouseEvent<HTMLElement>;
    }) => void;
    isAddingItemToCart: boolean;
} => {
    const dispatch = useDispatch();
    const item = useFragment(itemFragment, itemRef);
    const { serviceId: itemId, defaultSkuId } = item || {};

    const [commitUserCartItemAdd, userCartItemAddInFlight] = useMutation(userCartItemAddMutation);
    const addItemToCart = useCallback(
        async ({
            type = 'CHECKOUT',
            event,
        }: {
            type?: 'CHECKOUT' | 'SAVE_FOR_LATER';
            event?: MouseEvent<HTMLElement>;
        }) => {
            const sharedParams = {
                skuId: skuId || defaultSkuId,
                quantity: parseInt(quantitySelected, 10) || 1,
                type,
            } as const;
            const skuIdParam = skuId || defaultSkuId;
            if (userId) {
                await commitUserCartItemAdd({
                    variables: {
                        input: {
                            itemId,
                            ...sharedParams,
                        },
                    },
                });
                onClick?.(event);
            } else if (!userId && itemId && skuIdParam) {
                dispatch(
                    setMultiCartItem({
                        id: itemId,
                        ...sharedParams,
                        skuId: skuIdParam,
                    })
                );
                onClick?.(event);
            }
        },
        [
            userId,
            itemId,
            skuId,
            quantitySelected,
            onClick,
            commitUserCartItemAdd,
            defaultSkuId,
            dispatch,
        ]
    );

    return {
        addItemToCart,
        isAddingItemToCart: userCartItemAddInFlight,
    };
};
